// /subscribe — where the trial lifecycle emails land (?st=<token>), and a
// universal subscribe URL for anyone with a funnel session in this browser.
//
// The email token makes the CTA work on ANY device; without one we fall back
// to the funnel session (same-browser case). Nothing here is optimistic about
// billing state: the server applies the same eligibility policy as every
// other checkout surface, and refusals render as calm copy, not errors.
import { useEffect, useState } from 'react';
import { exchangeSubscribeToken, ApiError, RateLimited } from '../lib/api';
import { beginCheckout } from '../lib/checkout';
import { track } from '../lib/analytics';
import { ClaimNotice } from '../claim/ClaimSuccess';
import { ClaimShell } from '../claim/ClaimShell';

type Notice = { title: string; body: string } | null;

export function SubscribeApp() {
  const token = new URLSearchParams(window.location.search).get('st') || '';
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    document.title = 'Your membership · Expy Health';
    track('subscribe_link_open');
  }, []);

  const go = async () => {
    setBusy(true);
    setNotice(null);
    try {
      if (token) {
        const { url } = await exchangeSubscribeToken(token);
        track('checkout_start');
        window.location.href = url;
        return;
      }
      // No token: same-browser fallback through the funnel session.
      await beginCheckout();
    } catch (e) {
      if (e instanceof RateLimited) {
        setNotice({
          title: 'One moment.',
          body: 'Too many attempts from this connection. Give it a few minutes and try again.',
        });
      } else if (e instanceof ApiError && (e.status === 404 || e.status === 410)) {
        setNotice({
          title: 'This link has expired.',
          body: 'Open the most recent email from Expy Health and use the link there. If you keep landing here, write to support@expyhealth.com and we will sort it out.',
        });
      } else if (e instanceof ApiError && (e.status === 403 || e.status === 409)) {
        setNotice({
          title: 'There is nothing to pay right now.',
          body: 'Your access continues as is. If that seems wrong, write to support@expyhealth.com.',
        });
      } else {
        setNotice({
          title: 'We could not start checkout.',
          body: 'Nothing was charged. Please try again in a minute, or write to support@expyhealth.com.',
        });
      }
      setBusy(false);
    }
  };

  if (notice) {
    return <ClaimNotice kind="patient" title={notice.title} body={notice.body} action={undefined} />;
  }

  return (
    <ClaimShell footnote="Checkout is handled securely by Stripe. We never see your card details.">
      <h1 className="pretty" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, margin: 0 }}>
        Pick up right where you left off.
      </h1>
      <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', lineHeight: 1.55, marginTop: 12 }}>
        Your plan, your progress and your history are saved. A membership keeps them going.
      </p>
      <p style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>
        $19.99 a month. Cancel anytime. You have not been charged.
      </p>
      <p style={{ margin: '24px 0 0' }}>
        <button
          type="button"
          onClick={go}
          disabled={busy}
          style={{
            background: 'var(--teal)',
            color: '#fff',
            fontSize: 16,
            fontWeight: 800,
            padding: '15px 30px',
            borderRadius: 10,
            border: 'none',
            cursor: busy ? 'wait' : 'pointer',
            boxShadow: 'var(--teal-shadow)',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Opening secure checkout…' : 'Set up my membership'}
        </button>
      </p>
    </ClaimShell>
  );
}
